import { teachingOn } from "./lianxi-yufa-teaching.js";
import {
  finishedLine,
  handedOverLine,
  progressLine,
} from "./lianxi-yufa-copy.js";
import { afterFinish, joined, offerAfterFinish } from "./lianxi-finish-copy.js";
import type { YufaExerciseProperties } from "./lianxi-yufa.js";
import type { ItemQueue } from "./lianxi-yufa-queue.js";

/**
 * The two lines a grammar exercise writes about where the reader is: the one
 * over the items, and the finish line under them. `./lianxi-yufa-copy.ts`
 * has the words and `./lianxi-yufa.ts` says when each is written.
 */

/** Say where the reader is, over the items. */
export function showProgress(
  parts: Pick<YufaExerciseProperties, "progress" | "items">,
  queue: ItemQueue,
): void {
  const meter = parts.progress.parentElement?.querySelector<HTMLElement>(
    "[data-cb-yufa-meter]",
  );

  meter?.style.setProperty(
    "--percentage",
    `${String((100 * queue.done()) / queue.total())}%`,
  );
  meter
    ?.querySelector('[role="progressbar"]')
    ?.setAttribute("aria-valuenow", String(queue.done()));
  parts.progress.textContent = progressLine(
    queue.done(),
    queue.total(),
    queue.returning(),
  );
}

/**
 * The finish line, where the finish is showing, saying the sentences are in
 * 复习 once the keeper has heard so.
 */
export function showFinished(
  parts: Pick<
    YufaExerciseProperties,
    "finished" | "finishedLine" | "items" | "keeper"
  >,
  queue: ItemQueue,
): void {
  if (parts.finished.hidden === true) {
    return;
  }

  const exhausted = queue.practice()?.exhausted.length ?? 0;
  const done =
    exhausted === 1
      ? "Exercise complete. 1 practice question still needs work. Review its source section before trying again."
      : exhausted > 1
        ? `Exercise complete. ${String(exhausted)} practice questions still need work. Review their source sections before trying again.`
        : finishedLine(queue.total());

  parts.finishedLine.textContent = joined(
    done,
    afterFinish(handedOverLine, parts.keeper),
  );
  offerAfterFinish(parts.finishedLine, parts.keeper);
  let review = parts.finished.querySelector<HTMLElement>(
    "[data-cb-yufa-review]",
  );
  if (review === null) {
    review = document.createElement("ul");
    review.dataset["cbYufaReview"] = "";
    parts.finished.append(review);
  }
  review.replaceChildren();
  const seen = new Set<string>();
  for (const at of queue.practice()?.exhausted ?? []) {
    const link = parts.items[at]?.querySelector<HTMLAnchorElement>(
      ".lianxi-yufa-article, [data-cb-yufa-why] a[href^='/grammar/']",
    );
    if (link === undefined || link === null || seen.has(link.href)) {
      continue;
    }
    seen.add(link.href);
    const entry = document.createElement("li");
    const copy = link.cloneNode(true) as HTMLAnchorElement;
    copy.textContent =
      teachingOn(parts.items[at]!)?.target.use ??
      `Review question ${String(at + 1)}`;
    entry.append(copy);
    review.append(entry);
  }
}
